import React, { ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft } from "lucide-react";
import { cn } from "../../utils/cn";
import { IconButton } from "../widgets/IconButton";

interface DrawerProps {
    isOpen: boolean;
    onClose: () => void;
    title: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    widthClassName?: string;
    bgClassName?: string; // Optional background color class
    level?: number; // Drawer nesting level: 1 (default), 2, 3, etc. Level >= 2 shows back button on all devices
}

export function Drawer({
    isOpen,
    onClose,
    title,
    children,
    footer,
    widthClassName = "sm:max-w-lg md:max-w-xl",
    bgClassName = "bg-bgb",
    level = 1,
}: DrawerProps) {
    const [shouldRender, setShouldRender] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const drawerRef = React.useRef<HTMLDivElement>(null);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const scrolled = e.currentTarget.scrollTop > 0;
        if (scrolled !== isScrolled) {
            setIsScrolled(scrolled);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            // Double-RAF: first frame lets React mount the element and the browser
            // paint its initial off-screen state; second frame fires after that paint,
            // so the CSS transition always sees a real before→after change.
            let rafId2: number;
            const rafId1 = requestAnimationFrame(() => {
                rafId2 = requestAnimationFrame(() => {
                    setIsVisible(true);
                });
            });

            const handleClickOutside = (event: MouseEvent) => {
                const target = event.target as HTMLElement;
                if (drawerRef.current && !drawerRef.current.contains(target)) {
                    let current: HTMLElement | null = target;
                    let shouldIgnore = false;
                    while (current) {
                        if (current.getAttribute?.("data-drawer-ignore") === "true") {
                            shouldIgnore = true;
                            break;
                        }

                        // If we click inside a drawer with higher level, don't close this drawer
                        const clickedDrawerLevel = current.getAttribute?.("data-drawer-level");
                        if (clickedDrawerLevel && parseInt(clickedDrawerLevel) > level) {
                            shouldIgnore = true;
                            break;
                        }

                        current = current.parentElement;
                    }

                    // If clicking completely outside, check if there's a higher-level drawer open
                    if (!shouldIgnore) {
                        const allDrawers = document.querySelectorAll('[data-drawer-level]');
                        for (const drawer of Array.from(allDrawers)) {
                            const drawerLevel = parseInt(drawer.getAttribute('data-drawer-level') || '1');
                            if (drawerLevel > level) {
                                // There's a higher-level drawer, don't close this one
                                shouldIgnore = true;
                                break;
                            }
                        }
                    }

                    if (!shouldIgnore) onClose();
                }
            };

            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                cancelAnimationFrame(rafId1);
                cancelAnimationFrame(rafId2);
                document.removeEventListener("mousedown", handleClickOutside);
            };
        } else {
            setIsVisible(false);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsScrolled(false); // Reset scroll state on close
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, onClose]);



    if (!shouldRender) return null;

    const drawerContent = (
        <div className="fixed inset-0 flex justify-end overflow-hidden pointer-events-none" style={{ zIndex: 99999 + (level - 1) * 10 }}>
            <div className={`fixed inset-0 bg-b/05 transition-opacity duration-300 ease-out ${isVisible ? "opacity-100" : "opacity-0"}`} />
            <div
                ref={drawerRef}
                data-drawer-level={level}
                className={`relative w-full ${widthClassName} h-full ${bgClassName} shadow-2xl flex flex-col border-l-[1px] border-b05 transition-[transform,opacity] duration-300 ease-out rounded-none pointer-events-auto [will-change:transform,opacity] [backface-visibility:hidden] ${isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
            >
                <div className={cn(
                    "flex items-center gap-3 px-4 sm:px-6 py-3 transition-colors duration-200 border-b",
                    isScrolled ? "border-b05" : "border-transparent"
                )}>
                    {/* For nested drawers (level >= 2), always show back button. For top-level (level 1), show back on mobile only */}
                    <IconButton
                        icon={<ChevronLeft />}
                        onClick={onClose}
                        size="md"
                        shape="circle"
                        title="返回"
                        className={level >= 2 ? "" : "sm:hidden"}
                    />
                    <h2 className="text-lg font-semibold text-b flex-1 min-w-0 truncate flex items-center gap-2">{title}</h2>
                    {/* Only show close button (X) for top-level drawers on desktop */}
                    {level === 1 && (
                        <IconButton
                            icon={<X />}
                            onClick={onClose}
                            size="md"
                            shape="circle"
                            title="关闭"
                            className="hidden sm:flex"
                        />
                    )}
                </div>
                <div
                    className="flex-1 overflow-y-auto scrollbar-hide"
                    onScroll={handleScroll}
                >
                    <div className="p-4 sm:p-6">
                        {children}
                    </div>
                </div>
                {footer && (
                    <div
                        className="flex-shrink-0 px-4 sm:px-6 pb-4 sm:pb-6 pt-3 sm:pt-4 flex gap-3 bg-inherit border-t border-b05 sm:border-t-0"
                        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
                    >
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(drawerContent, document.body);
}


// Re-export specific drawer content components
export { Header } from '../content/Header';

